import React, { useEffect, useState, useContext, useRef } from "react";
import ActionsContext from "common/ActionsContext";
import Modal from "common/Modal.jsx";
import FileInput from "common/FileInput";
import styled from "styled-components";

const SOFTWARE_INSTALL_TMOUT_MS = 300000;
const CHUNKMAXSIZE = 32 * 1024;
const doesNothing = () => {};
const wontCancel = { current: false };

const fromBinaryToBase64 = function (buf) {
  var bytes = new Uint8Array(buf),
    len = bytes.byteLength,
    charArray = new Array(len);
  for (var i = 0; i < len; i++) {
    charArray[i] = String.fromCharCode(bytes[i]);
  }
  return window.btoa(charArray.join(""));
};

const createRemoteTmpPath = () =>
  "/tmp/sw_" + Math.random().toString(36).substring(5);

const installBpkg = (remotePath) =>
  new Promise((resolve, reject) => {
    console.log("software.install.start");
    ifCl.executeWithOpts(
      "system software install " + remotePath,
      SOFTWARE_INSTALL_TMOUT_MS,
      () => reject("timeout during install"),
      (res) => {
        const { Name, Version } = res.CmdResponse.response
          .trim("\n")
          .split("\n")
          .reduce((acc, row) => {
            if (row.startsWith("Name:") || row.startsWith("Version:")) {
              const [key, value] = row.split(":");
              if (value !== undefined) {
                acc[key.trim()] = value.trim();
              }
            }
            return acc;
          }, {});
        if (
          res.CmdResponse.code !== 0 ||
          res.CmdResponse.response.indexOf("%ERR") > -1
        ) {
          console.log("software.install.error");
          reject(`Error installing software: ${res.CmdResponse.response}`);
        } else {
          console.log("software.install.done");
          resolve({ Name, Version });
        }
      }
    );
  });

const uploadInto = (
  remotePath,
  source,
  { cancelledRef = wontCancel, progress = doesNothing }
) => {
  let offset = 0;
  progress(offset, source.size);
  const reader = new FileReader();
  return new Promise((resolve, reject) => {
    const next = () => {
      const blob = source.slice(offset, offset + CHUNKMAXSIZE);
      reader.readAsArrayBuffer(blob);
    };
    reader.onerror = (event) => reject(event.target.error);
    reader.onload = (event) => {
      if (cancelledRef.current === true) {
        reject("Cancelled");
        return;
      }
      if (offset >= source.size) {
        resolve(remotePath);
        return;
      }
      // File Chunk available
      const chunkInB64 = fromBinaryToBase64(event.target.result);
      ifCl.saveFile(
        chunkInB64.length,
        chunkInB64,
        // remotePath MUST be null after first chunk
        offset === 0 ? remotePath : null,
        (error) => reject(error),
        (res) => {
          if (res.SaveFileResponse.code === 0) {
            // Read next chunk
            offset += event.target.result.byteLength;
            progress(offset, source.size);
            next();
          } else {
            reject(`Failed while sending ${source.name}`);
          }
        }
      );
    };
    next();
  });
};

const SourceFileInput = ({ name, onLoad = doesNothing }) => {
  const input = useRef(null);
  const handleSourceChange = (event) => {
    const [source] = event.target.files;
    onLoad(source);
  };
  useEffect(() => {
    if (input.current) input.current.click();
  }, [input.current]);
  return (
    <FileInput
      inputRef={input}
      text="Browse..."
      label={null}
      name={name}
      onChange={handleSourceChange}
    />
  );
};

const useInstall = ({ whenDone = doesNothing }) => {
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(null);
  const cancelledRef = useRef(false);
  const cancel = () => (cancelledRef.current = true);
  const unCancel = () => (cancelledRef.current = false);
  const clear = () => {
    unCancel();
    setError(null);
    setProgress(null);
  };
  const reportError = (error) => {
    setProgress(null);
    setError(error);
  };
  useEffect(() => clear(), []);
  const uploadProgress = (progress, total) =>
    setProgress({ upload: { progress, total } });
  const start = (file) => {
    return Promise.resolve(clear())
      .then(createRemoteTmpPath)
      .then((remotePath) =>
        uploadInto(remotePath, file, { cancelledRef, progress: uploadProgress })
      )
      .then(
        (remotePath) => (
          setProgress({ upload: true, installing: true }),
          installBpkg(remotePath)
        )
      )
      .then((response) =>
        setProgress((previous) => ({
          ...previous,
          completed: true,
          name: file.name,
          nameBpkg: response.Name,
          versionBpkg: response.Version,
        }))
      )
      .then(whenDone)
      .catch(reportError);
  };

  return [clear, start, cancel, { progress, error }];
};

const TotalBar = styled.div`
  height: 1em;
  width: auto;
  border-color: var(--Text-Primary);
  border: solid 1px;
`;

const ProgressBar = styled.div`
  position: relative;
  height: 1em;
  background-color: var(--Text-Primary);
`;

const ContentWrapper = styled.div`
  & div {
    display: flex;
    align-items: center;
    flex-direction: row;
  }

  i {
    font-size: 20px;
    line-height: 20px;
    margin: 0 10px;
  }
`;

const UploadProgress = ({ progress = 0, total = 1 }) => (
  <>
    <h4>Transferring software file...</h4>
    <TotalBar alt={`${progress} bytes of ${total}`}>
      <ProgressBar style={{ width: `${(progress / total) * 100}%` }} />
    </TotalBar>
  </>
);

const ReportProgress = ({
  upload = false,
  installing = false,
  completed = false,
  name,
  nameBpkg,
  versionBpkg,
  loading,
}) => {
  const isReloadPackage = nameBpkg === "gui" || nameBpkg === "bqn";
  const content = isReloadPackage ? (
    <div>
      <ul>
        <li>
          Click on ACTIVATE to activate it now. Activation will interrupt
          traffic for 10-20 seconds.
        </li>
        <li>
          Click on CLOSE if you prefer to activate it later. You can then
          activate the installed package from this page by clicking on the{" "}
          <i className="material-icons valign-icon">cached</i> icon in the
          ACTIONS column.
        </li>
      </ul>
    </div>
  ) : (
    <div>A reboot is required to activate this package.</div>
  );

  if (completed) {
    return (
      <ContentWrapper>
        <h4>Package {name} installed correctly</h4>
        {!loading ? content : null}
      </ContentWrapper>
    );
  }
  if (installing) {
    return <h4>Installing...</h4>;
  }
  if (upload && upload !== true) {
    return <UploadProgress {...upload} />;
  }
  return null;
};

const getApply = ({
  isUploading,
  isCompleted,
  loading,
  cancel,
  doActivate,
  isReloadPackage,
}) => {
  if (isUploading) {
    return {
      applyLabel: "CANCEL",
      onApply: cancel,
    };
  }
  if (isCompleted) {
    if (loading) {
      return {
        applyLabel: null,
        onApply: null,
      };
    }
    if (isReloadPackage) {
      return {
        applyLabel: "ACTIVATE",
        onApply: doActivate,
      };
    }
  }
  return {
    applyLabel: null,
    onApply: null,
  };
};

const InstallModal = () => {
  const [open, setOpen] = useState(false);
  const actions = useContext(ActionsContext);
  const [loading, setLoading] = useState(false);
  const [errorFooter, setErrorFooter] = useState("");

  const whenDone = () => actions.send("do-load");
  const [clear, start, cancel, { error, progress }] = useInstall({ whenDone });

  const doOpen = () => {
    clear();
    setOpen(true);
  };
  const doClose = () => {
    setOpen(false);
  };

  const doActivate = () => {
    setErrorFooter("");
    setLoading(true);
    return ifCl
      .run(
        `system software reload ${progress.nameBpkg} ${progress.versionBpkg} force`
      )
      .then((response) => {
        if (response) {
          console.log("software reload .<%s>.error: <%s>", response);
          throw `Warning: ${response}`;
        }
      })
      .catch((error) => {
        setLoading(false);
        setErrorFooter(error);
      });
  };

  useEffect(() => actions.recv(`install-software`, () => doOpen()), []);
  if (open === false) {
    return null;
  }
  const isUploading =
    progress !== null && progress.upload && progress.upload !== true;
  const isCompleted =
    progress !== null && progress.completed && progress.completed === true;
  const isReloadPackage =
    progress !== null && (progress.nameBpkg === "gui" || progress.nameBpkg === "bqn");  
  const { applyLabel, onApply } = getApply({
    isUploading,
    isCompleted,
    loading,
    cancel,
    doActivate,
    isReloadPackage,
  });
  return (
    <Modal
      title="Install .bpkg file"
      className="modal-software-install"
      superIcon="add_circle"
      content={() => (
        <>
          {progress === null ? (
            <SourceFileInput name="source" onLoad={start} />
          ) : (
            <>
              <ReportProgress {...progress} loading={loading} />
              {loading ? (
                <div>
                  <b>Activating</b>: Wait a few seconds and refresh the browser.
                </div>
              ) : null}
            </>
          )}
          {error === null
            ? null
            : console.error(error) || (
                <div className="margin-t-15 color-red text-align-left pre-wrap">
                  {String(error)}
                </div>
              )}
        </>
      )}
      footerMessage={
        errorFooter !== "" ? (
          <span className="modal-err-msg color-red">
            <h5>{errorFooter}</h5>
          </span>
        ) : null
      }
      applyLabel={applyLabel}
      onApply={onApply}
      closeLabel={isUploading ? null : "CLOSE"}
      onClose={isUploading ? null : doClose}
    />
  );
};

export default InstallModal;
